/* eslint-disable */
// @flow
/** @jsx jsx */

import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { jsx } from '@emotion/core'
import { Query } from 'react-apollo'
import cx from 'classnames'
import type { GridItem } from '../../types'
import GridApi from '../../components/GridApi'
import PageContainer from '../../components/PageContainer'
import ActionsHeader from '../../components/ActionsHeader'
import ModuleQuery from '../../components/ModuleQuery'
import Row from '../../components/Row'
import Col from '../../components/Col'
import Notifications from '../../components/Notifications'
import ContentChecker from '../../components/ContentChecker'
import FullSizeCenteredContent from '../../components/FullSizeCenteredContent'
import Loader from '../../components/Loading'
import GridApiController from '../../controllers/GridApiController'
import userSettingsQuery from '../../graphql/queries/userSettings.query'
import moduleDataQuery from '../../graphql/queries/itemHistoryData.query'
import ActionsOrganizer from './components/ActionsOrganizer'
import { emitter, getDefaultUserSettings } from '../../utils'
import { OrderContentConsumer } from '../../context/orderContent.context'
import inventoryDataQuery from '../../graphql/queries/inventoryData.query'
import getActiveOrderQuery from '../../graphql/queries/getActiveOrder.query'
import { MobileView, useMobileMediaQuery } from '../../config/mediaQuery'
import ModuleMenuBar from '../../components/ModuleMenuBar'
import { ActionsContext } from '../../context/actions.context';
import useMenuHandler from '../../hooks/useMenuHandler';

type Props = {
  isFullPage?: boolean,
  selectedRow?: GridItem,
  showGroupPanel?: boolean,
  paginated?: boolean,
  orderRefetch: () => {},
  accountName: String,
}

const OrderItemsHistory = (props: Props) => {
  const moduleName = 'itemHistoryOrder';

  const menuRef = useRef();
  const menuButtonRef = useRef();
  const { allScreens = [] } = useContext(ActionsContext);

  const {
    isFullPage = false,
    showGroupPanel = true,
    paginated = false,
    accountName
  } = props;

  const isMobile = useMobileMediaQuery();
  const [displayMenu, setDisplayMenu] = useState(false);
  const [handlerAdded, setHandlerAdded] = useState(false);

  const changeDisplayMenu = (status) => {
    setDisplayMenu(status);
  }

  const handler = useCallback((e) => {
    if (menuRef.current
      && menuButtonRef.current
      && !menuRef.current.contains(e.target)
      && !menuButtonRef.current.contains(e.target)
    ) {
      setDisplayMenu(value => {
        if (value) {
          return false;
        }
        return value;
      });
    }
  }, []);

  useMenuHandler(handler, allScreens.length === 1, setHandlerAdded);

  return (
    <PageContainer
      className={cx({
        'page--is-full-page': isFullPage,
        'page--is-nested': !isFullPage,
      })}
      isFullPage={isFullPage}
    >
      <OrderContentConsumer>
        {({
          selectedOrderType,
          selectedOrderDate,
          orderItems,
          onChangeOrderItems,
          hasLocalOrderItems,
        }: any) => {
          return (
            <ModuleQuery
              query={moduleDataQuery}
              variables={{
                moduleName: 'itemHistory',
                selectedRow: props.selectedRow,
              }}
            >
              {({
                data: moduleData,
                loading: moduleLoading,
                refetch: moduleRefetch,
                refetching: moduleRefetching,
                refetched: moduleRefetched,
              }) => (
                <Query
                  query={userSettingsQuery}
                  variables={{
                    moduleName: moduleName,
                  }}
                >
                  {({ data: dataGrid, loading }: any) => {
                    if (loading || !dataGrid) {
                      return (
                        <FullSizeCenteredContent>
                          <Loader />
                        </FullSizeCenteredContent>
                      )
                    }

                    const userSettings =
                      dataGrid && dataGrid.userSettings
                        ? {
                          columnsMapping: dataGrid.userSettings.columns,
                          frozenColumns:
                            dataGrid.userSettings.frozenColumns,
                          frozenRows: dataGrid.userSettings.frozenRows,
                          groups: dataGrid.userSettings.groups,
                          sorting: dataGrid.userSettings.sorting,
                        }
                        : {}

                    const defaultUserSettings = getDefaultUserSettings(
                      dataGrid,
                    )

                    return (
                      <Query query={getActiveOrderQuery}>
                        {({ data, loading: activeOrderLoading }: any) => {
                          const { activeOrder } = data
                          const activeOrderSource = activeOrder || {}

                          const onAddItemComplete =
                            activeOrderSource &&
                              activeOrderSource.Account &&
                              activeOrderSource.Account.Level === 'J'
                              ? () => {
                                emitter.emit('pst-refetch')
                              }
                              : () => {
                                emitter.emit('pst-check-refetch')
                              }

                          return (
                            <ModuleQuery
                              query={inventoryDataQuery}
                              variables={{
                                selectedOrderDate,
                                selectedOrderType,
                                state: activeOrderSource.Account.Level,
                              }}
                            >
                              {({
                                data: inventoryData,
                                loading: loadingInventory,
                                refetch: inventoryRefetch,
                                refetching: inventoryRefetching,
                              }) => {
                                const combineData = (
                                  firstArray,
                                  secondArray,
                                ) => {
                                  return firstArray.map((obj) => {
                                    const matchingObj = secondArray.find(
                                      (item) => item.Code === obj.Code,
                                    )

                                    if (matchingObj) {
                                      return {
                                        ...obj,
                                        ...matchingObj,
                                        LastPrice: obj.Price,
                                      }
                                    }

                                    return {
                                      Code: obj.Code,
                                      LastPrice: obj.Price,
                                      AcctNo: obj.AcctNo,
                                      Date: obj.Date,
                                      Qty: obj.Qty,
                                      Uom: obj.Uom,
                                      Price: {
                                        value: obj.Price,
                                        rawValue: String(obj.Price),
                                        product: {
                                          Uom: obj.Uom
                                        },
                                        pricingTiers: [
                                          {
                                            tier: 0,
                                            price: obj.Price,
                                          },
                                        ],
                                      },
                                      Description: obj.Description,
                                      maxItemQty: {
                                        byOrderType: [
                                          {
                                            id: 'S',
                                            Qty: 0,
                                            Cases: 0,
                                            Bottles: 0,
                                          },
                                          {
                                            id: 'M',
                                            Qty: 0,
                                            Cases: 0,
                                            Bottles: 0,
                                          },
                                          {
                                            id: 'BO',
                                            Qty: 0,
                                            Cases: 0,
                                            Bottles: 0,
                                          },
                                          {
                                            id: 'P',
                                            Qty: 9007199254740991,
                                            Cases: 2147483647,
                                            Bottles: 2147483647,
                                          },
                                          {
                                            id: 'U',
                                            Qty: 9007199254740991,
                                            Cases: 2147483647,
                                            Bottles: 2147483647,
                                          },
                                          {
                                            id: 'BHS',
                                            Qty: 0,
                                            Cases: 0,
                                            Bottles: 0,
                                          },
                                          {
                                            id: 'BHI',
                                            Qty: 0,
                                            Cases: 0,
                                            Bottles: 0,
                                          },
                                        ],
                                        PreviousAdd: {
                                          Qty: 0,
                                          Cases: 0,
                                          Bottles: 0,
                                        },
                                      },
                                    }
                                  })
                                }

                                return (
                                  <GridApiController
                                    data={
                                      moduleData.itemHistoryContent &&
                                      inventoryData.inventoryContent &&
                                      combineData(
                                        moduleData.itemHistoryContent,
                                        inventoryData.inventoryContent,
                                      )
                                    }
                                    loading={
                                      loading ||
                                      moduleLoading ||
                                      activeOrderLoading ||
                                      loadingInventory
                                    }
                                    refetching={
                                      moduleRefetching || inventoryRefetching
                                    }
                                    refetched={moduleRefetched}
                                    refetch={moduleRefetch}
                                    defaultColumns={
                                      defaultUserSettings.defaultColumns
                                    }
                                    defaultSorting={
                                      defaultUserSettings.defaultSorting
                                    }
                                    defaultGroupPanels={
                                      defaultUserSettings.defaultGroupPanels
                                    }
                                    orderItems={orderItems}
                                    onChangeOrderItems={onChangeOrderItems}
                                    hasLocalOrderItems={hasLocalOrderItems}
                                    paginated={paginated}
                                    isMobile={isMobile}
                                    {...userSettings}
                                  >
                                    {({
                                      selectedItems,
                                      collection,
                                      columnsMapping,
                                      currentItem,
                                      onGridUpdated,
                                      onGridRef,
                                      onGridWillUnmount,
                                      onColSorted,
                                      onColResized,
                                      onColDragged,
                                      onColFreezed,
                                      onRowFreezed,
                                      onRowSelected,
                                      onColumnsMappingChange,
                                      onCurrentItemChanged,
                                      onGroupPanelChanged,
                                      onClearSelection,
                                      onNotesSaved,
                                      saveStatus,
                                      onViewSaved,
                                      onViewReset,
                                      grid,
                                      userViewState,
                                      hasAllColumnsHidden,
                                      frozenColumns,
                                      frozenRows,
                                      refetch,
                                      isLoading,
                                      isRefetching,
                                      hasDataToDisplay,
                                      playResetAnimation,
                                      handleResetAnimation,
                                      setFlexGridFilter,
                                      clearInternalFilters,
                                      onItemAdded,
                                      itemAddedStatus,
                                      onItemUpdated,
                                      toggleMultiSort,
                                      multiColumnSort
                                    }) => {
                                      return (
                                        <React.Fragment>
                                          <Notifications
                                            saveStatus={saveStatus}
                                          />
                                          <ActionsHeader
                                            grid={grid}
                                            collection={collection}
                                            defaultColumnsMapping={
                                              defaultUserSettings.defaultColumns
                                            }
                                            columnsMapping={columnsMapping}
                                            selectedItems={selectedItems}
                                            moduleName={moduleName}
                                            saveStatus={saveStatus}
                                            loading={
                                              isLoading || isRefetching
                                            }
                                            onViewSaved={onViewSaved}
                                            onViewReset={onViewReset}
                                            onNotesSaved={onNotesSaved}
                                            onColumnsMappingChange={
                                              onColumnsMappingChange
                                            }
                                            userViewState={userViewState}
                                            refetch={refetch}
                                            hasAllColumnsHidden={
                                              hasAllColumnsHidden
                                            }
                                            hasDataToDisplay={
                                              hasDataToDisplay
                                            }
                                            frozenColumns={frozenColumns}
                                            frozenRows={frozenRows}
                                            currentItem={currentItem}
                                            defaultSettings={
                                              defaultUserSettings
                                            }
                                            handleResetAnimation={
                                              handleResetAnimation
                                            }
                                            useLegacyColumnsManager={true}
                                            toggleMultiSort={toggleMultiSort}
                                            multiColumnSort={multiColumnSort}
                                          >
                                            {(actions) => {
                                              const { isOneRowSelected, ColumnsAction } = actions;
                                              return (
                                                <>
                                                  <MobileView>
                                                    <div className='module-title'>{accountName}</div>
                                                    <ModuleMenuBar
                                                      moduleRefetch={moduleRefetch}
                                                      changeDisplayMenu={changeDisplayMenu}
                                                      ColumnsAction={ColumnsAction}
                                                      displayMenu={displayMenu}
                                                      menuButtonRef={menuButtonRef}
                                                      clearInternalFilters={clearInternalFilters}
                                                    />
                                                  </MobileView>
                                                  <div ref={menuRef}>
                                                    <Row
                                                      css={{
                                                        justifyContent:
                                                          'space-between',
                                                        padding: '0 10px',
                                                        ...(isMobile && {
                                                          position: 'absolute',
                                                          bottom: 0,
                                                          boxShadow:
                                                            '0px -4px 4px 0px #ACACAC40',
                                                          zIndex: 100,
                                                          background: '#fff',
                                                          borderRadius: '8px 8px 0px 0px',
                                                          width: '100%',
                                                          padding: '10px 20px',
                                                          display: displayMenu || isOneRowSelected
                                                            ? 'flex'
                                                            : 'none',
                                                          justifyContent: 'center',
                                                          height: (displayMenu && '189px') || (isOneRowSelected && '229px')
                                                        }),
                                                      }}
                                                    >
                                                      <Col>
                                                        <ActionsOrganizer
                                                          minHeight='80px'
                                                          clearInternalFilters={
                                                            clearInternalFilters
                                                          }
                                                          currentItem={currentItem}
                                                          itemHistoryRefetch={
                                                            refetch
                                                          }
                                                          inventoryRefetch={
                                                            inventoryRefetch
                                                          }
                                                          orderRefetch={
                                                            props.orderRefetch
                                                          }
                                                          loadingInventory={
                                                            loadingInventory
                                                          }
                                                          inventoryRefetching={
                                                            inventoryRefetching
                                                          }
                                                          onAddItemComplete={
                                                            onAddItemComplete
                                                          }
                                                          activeOrderSource={
                                                            activeOrderSource
                                                          }
                                                          onItemAdded={onItemAdded}
                                                          itemAddedStatus={
                                                            itemAddedStatus
                                                          }
                                                          onItemUpdated={
                                                            onItemUpdated
                                                          }
                                                          moduleName={
                                                            moduleName
                                                          }
                                                          selectedOrderDate={
                                                            selectedOrderDate
                                                          }
                                                          selectedOrderType={
                                                            selectedOrderType
                                                          }
                                                          loading={isRefetching || isLoading}
                                                          {...actions}
                                                          displayRowMenu={isOneRowSelected}
                                                          displayMenu={displayMenu}
                                                          toggleMultiSort={toggleMultiSort}
                                                        />
                                                      </Col>
                                                    </Row>
                                                  </div>
                                                </>
                                              )
                                            }}
                                          </ActionsHeader>
                                          <ContentChecker
                                            isLoading={isLoading}
                                            isRefetching={isRefetching}
                                            hasAllColumnsHidden={
                                              hasAllColumnsHidden
                                            }
                                            hasDataToDisplay={
                                              hasDataToDisplay
                                            }
                                            collection={collection}
                                          >
                                            {() => (
                                              <React.Fragment>
                                                <GridApi
                                                  collection={collection}
                                                  columnsMapping={
                                                    columnsMapping
                                                  }
                                                  onRowSelected={
                                                    onRowSelected
                                                  }
                                                  onColSorted={onColSorted}
                                                  onColResized={onColResized}
                                                  onColDragged={onColDragged}
                                                  onColFreezed={onColFreezed}
                                                  onRowFreezed={onRowFreezed}
                                                  onGridUpdated={
                                                    onGridUpdated
                                                  }
                                                  onGridWillUnmount={
                                                    onGridWillUnmount
                                                  }
                                                  onCurrentItemChanged={
                                                    onCurrentItemChanged
                                                  }
                                                  onGroupPanelChanged={
                                                    onGroupPanelChanged
                                                  }
                                                  onClearSelection={
                                                    onClearSelection
                                                  }
                                                  ref={onGridRef}
                                                  refetching={isRefetching}
                                                  hasAllColumnsHidden={
                                                    hasAllColumnsHidden
                                                  }
                                                  hasDataToDisplay={
                                                    hasDataToDisplay
                                                  }
                                                  showFrozenBars={false}
                                                  isFullPage={
                                                    isFullPage
                                                  }
                                                  showGroupPanel={
                                                    showGroupPanel
                                                  }
                                                  paginated={
                                                    paginated
                                                  }
                                                  playResetAnimation={
                                                    playResetAnimation
                                                  }
                                                  setFlexGridFilter={
                                                    setFlexGridFilter
                                                  }
                                                  menuRef={menuRef}
                                                  isMobile={isMobile}
                                                  handlerAdded={handlerAdded}
                                                  multiColumnSort={multiColumnSort}
                                                />
                                              </React.Fragment>
                                            )}
                                          </ContentChecker>
                                        </React.Fragment>
                                      )
                                    }}
                                  </GridApiController>
                                )
                              }}
                            </ModuleQuery>
                          )
                        }}
                      </Query>
                    )
                  }}
                </Query>
              )}
            </ModuleQuery>
          )
        }}
      </OrderContentConsumer>
    </PageContainer>
  )
}

export default OrderItemsHistory;